import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../api';
import { useAuth } from '../AuthContext';
import { PAGE_ONLY } from '../schemas';
import { I18nInput } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';

let _uidSeq = 0;
const uid = () => `tl-${Date.now().toString(36)}-${_uidSeq++}`;

// Editable hero section (eyebrow, title lines/single, text, image).
function HeroEditor({ hero, titleKeys, tenantId, onHeroChange, onTitleLine, onAddTitleLine, onRemoveTitleLine }) {
  const titleIsArray = Array.isArray(hero.title);
  return (
    <div className="rounded-2xl border border-paelbi-line bg-white p-6">
      <h3 className="font-display text-lg text-paelbi-ink">Sezione Hero</h3>
      <div className="mt-4 space-y-4">
        {('eyebrow' in hero) && <I18nInput label="Eyebrow" value={hero.eyebrow} onChange={(v) => onHeroChange({ eyebrow: v })} />}
        {titleIsArray ? (
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-medium text-paelbi-ink/80">Titolo (righe)</span>
              <button type="button" data-testid="hero-add-title-line" onClick={onAddTitleLine} className="text-xs font-semibold text-paelbi-brown">+ Aggiungi riga</button>
            </div>
            <div className="space-y-2">
              {hero.title.map((line, i) => (
                <div key={titleKeys[i] || i} className="flex items-start gap-2">
                  <div className="flex-1"><I18nInput label={`Riga ${i + 1}`} value={line} onChange={(v) => onTitleLine(i, v)} /></div>
                  <button type="button" data-testid={`hero-remove-title-line-${i}`} onClick={() => onRemoveTitleLine(i)} className="mt-6 text-xs text-red-500">rimuovi</button>
                </div>
              ))}
            </div>
          </div>
        ) : ('title' in hero) && <I18nInput label="Titolo" value={hero.title} onChange={(v) => onHeroChange({ title: v })} />}
        {('text' in hero) && <I18nInput label="Testo" value={hero.text} multiline onChange={(v) => onHeroChange({ text: v })} />}
        {('description' in hero) && <I18nInput label="Descrizione" value={hero.description} multiline onChange={(v) => onHeroChange({ description: v })} />}
        {('image' in hero) && <ImagePicker label="Immagine" value={hero.image} tenantId={tenantId} onChange={(v) => onHeroChange({ image: v })} />}
      </div>
    </div>
  );
}

// Friendly hero editor + advanced JSON. Reusable across page-only and section pages.
export function PageSettingsForm({ pageKey, tenantId }) {
  const [data, setData] = useState(null);
  const [json, setJson] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const titleKeysRef = useRef([]);

  const load = useCallback(async () => {
    const doc = await adminApi.getPage(pageKey, tenantId);
    const d = doc.data || {};
    titleKeysRef.current = Array.isArray(d.hero?.title) ? d.hero.title.map(() => uid()) : [];
    setData(d);
    setJson(JSON.stringify(d, null, 2));
  }, [pageKey, tenantId]);

  useEffect(() => { load(); }, [load]);

  const setHero = (patch) => {
    const next = { ...data, hero: { ...(data.hero || {}), ...patch } };
    setData(next); setJson(JSON.stringify(next, null, 2));
  };

  const setTitleLine = (idx, v) => {
    const arr = Array.isArray(data.hero?.title) ? [...data.hero.title] : [];
    arr[idx] = v; setHero({ title: arr });
  };
  const addTitleLine = () => {
    titleKeysRef.current = [...titleKeysRef.current, uid()];
    setHero({ title: [...(Array.isArray(data.hero?.title) ? data.hero.title : []), { it: '' }] });
  };
  const removeTitleLine = (idx) => {
    titleKeysRef.current = titleKeysRef.current.filter((_, i) => i !== idx);
    setHero({ title: data.hero.title.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const payload = showJson ? JSON.parse(json) : data;
      await adminApi.putPage(pageKey, payload, tenantId);
      titleKeysRef.current = Array.isArray(payload.hero?.title) ? payload.hero.title.map(() => uid()) : [];
      setData(payload); setJson(JSON.stringify(payload, null, 2));
      setMsg('Salvato con successo');
    } catch (e) {
      setMsg('Errore: ' + (e.message || 'JSON non valido'));
    } finally { setSaving(false); }
  };

  if (!data) return <p className="text-sm text-paelbi-ink/50">Caricamento...</p>;
  const hero = data.hero || {};

  return (
    <div className="space-y-6" data-testid="page-settings-form">
      {(data.hero !== undefined) && (
        <HeroEditor
          hero={hero}
          titleKeys={titleKeysRef.current}
          tenantId={tenantId}
          onHeroChange={setHero}
          onTitleLine={setTitleLine}
          onAddTitleLine={addTitleLine}
          onRemoveTitleLine={removeTitleLine}
        />
      )}

      <div className="rounded-2xl border border-paelbi-line bg-white p-6">
        <button type="button" onClick={() => setShowJson((v) => !v)} className="text-sm font-semibold text-paelbi-brown">
          {showJson ? 'Nascondi' : 'Mostra'} editor avanzato (JSON)
        </button>
        <p className="mt-1 text-xs text-paelbi-ink/50">Modifica tutti i blocchi (filtri, benefit, banner, ecc.). I testi multilingua sono oggetti {'{'}"it":"...","en":"..."{'}'}.</p>
        {showJson && (
          <textarea data-testid="page-json-editor" value={json} onChange={(e) => setJson(e.target.value)} rows={18}
            className="mt-3 w-full rounded-lg border border-paelbi-line bg-paelbi-bg p-3 font-mono text-xs outline-none focus:border-paelbi-brown" />
        )}
      </div>

      <div className="flex items-center gap-4">
        <button type="button" data-testid="page-save-button" onClick={save} disabled={saving} className="rounded-btn bg-paelbi-brown px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva pagina'}</button>
        {msg && <span className={`text-sm ${msg.startsWith('Errore') ? 'text-red-600' : 'text-green-700'}`}>{msg}</span>}
      </div>
    </div>
  );
}

function resolveScope(meta, isSuper, tenantId) {
  if (meta.scope === 'global') return undefined;
  if (isSuper) return tenantId;
  return undefined;
}

export function PageEditor({ pageKey }) {
  const { isSuper, tenantId } = useAuth();
  const meta = PAGE_ONLY[pageKey] || { label: pageKey, scope: 'tenant' };
  const scope = resolveScope(meta, isSuper, tenantId);
  // Super admin editing a tenant-scoped page needs the active tenant resolved
  // first, otherwise the initial load would fire without tenant_id.
  const waitingForTenant = isSuper && meta.scope !== 'global' && !tenantId;
  return (
    <div>
      <h1 className="font-display text-3xl text-paelbi-ink">{meta.label}</h1>
      <p className="mt-1 text-paelbi-ink/60">Scrivi i contenuti in italiano: le altre lingue vengono tradotte automaticamente.</p>
      <div className="mt-6">
        {waitingForTenant
          ? <p className="text-sm text-paelbi-ink/50">Caricamento...</p>
          : <PageSettingsForm pageKey={pageKey} tenantId={scope} />}
      </div>
    </div>
  );
}

export default PageEditor;
